import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDiamondTurnRight } from "@fortawesome/pro-solid-svg-icons/faDiamondTurnRight";
import { findIconByName } from "../../../data/fontawesome-icons";
import { analytics } from "../../../lib/analytics";
import { contrastText } from "../../../lib/color-utils";
import { cn } from "@wondermaps/ui";
import { useTranslation } from "react-i18next";
import { toast } from "@wondermaps/ui";
import type { POILink } from "@wondermaps/db";

interface LinkPillRowProps {
  links: POILink[];
  onGetDirections?: () => void;
  onRequestLocation?: () => void;
  hasPathNetwork?: boolean;
  gpsActive?: boolean;
  brandAccent?: string | null;
}

export function LinkPillRow({
  links,
  onGetDirections,
  onRequestLocation,
  hasPathNetwork,
  gpsActive,
  brandAccent,
}: LinkPillRowProps) {
  const { t } = useTranslation("pois");

  const handleDirectionsClick = () => {
    if (gpsActive && hasPathNetwork && onGetDirections) {
      onGetDirections();
    } else if (!gpsActive && onRequestLocation) {
      onRequestLocation();
    } else if (gpsActive && !hasPathNetwork) {
      toast({ title: t("noPathNetwork") });
    } else if (!gpsActive) {
      // GPS not active, no onRequestLocation handler — show denied toast
      toast({ title: t("locationDeniedSettings") });
    }
  };

  const sortedLinks = [...links].sort((a, b) => a.sortOrder - b.sortOrder);

  // 0 links → directions full-width. 1 link → 50/50 split, no scroll.
  // 2+ links → horizontal scroll (scroll affordance signals more content).
  const variant: "full" | "split" | "scroll" =
    links.length === 0 ? "full" : links.length === 1 ? "split" : "scroll";

  const containerClass =
    variant === "scroll"
      ? "flex overflow-x-auto gap-2 scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      : "flex gap-2";

  const directionsWidthClass =
    variant === "full"
      ? "w-full justify-center"
      : variant === "split"
        ? "flex-1 justify-center min-w-0"
        : "shrink-0";

  const linkWidthClass =
    variant === "split" ? "flex-1 justify-center min-w-0" : "shrink-0";

  return (
    <div className={containerClass}>
      {/* Directions pill — always first */}
      <button
        type="button"
        onClick={handleDirectionsClick}
        className={cn(
          "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap",
          !brandAccent && "bg-teal-600 text-white",
          directionsWidthClass
        )}
        style={brandAccent ? { backgroundColor: brandAccent, color: contrastText(brandAccent) } : undefined}
      >
        <FontAwesomeIcon icon={faDiamondTurnRight} className="h-4 w-4" />
        {t("directions")}
      </button>

      {/* Link pills */}
      {sortedLinks.map((link) => {
        const isExternal = !link.url.startsWith("/");
        const iconEntry = link.icon ? findIconByName(link.icon) : null;

        const handleClick = () => {
          if (isExternal) {
            analytics.clickOutbound({
              url: link.url,
              label: link.label,
              location: "poi_button",
            });
          }
        };

        return (
          <a
            key={link.id}
            href={link.url}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            onClick={handleClick}
            className={cn(
              "inline-flex items-center gap-2 bg-foreground/[0.08] text-foreground rounded-full px-4 py-2 text-sm font-medium",
              variant === "split" ? "min-w-0" : "whitespace-nowrap",
              linkWidthClass
            )}
          >
            {iconEntry && (
              <FontAwesomeIcon icon={iconEntry.icon} className="h-4 w-4 shrink-0" />
            )}
            <span className={variant === "split" ? "truncate" : undefined}>
              {link.label}
            </span>
          </a>
        );
      })}
    </div>
  );
}
